<script setup>
// Read-only knowledge base viewer for any logged-in member (agents included).
// Embeds the private /hc portal inside the dashboard main area (left sidebar is
// kept) via a same-origin iframe. We obtain an access ticket first (sets the
// hc_access cookie), then point the iframe at the portal and pass the current
// dashboard theme. Browsing/isolation/read-only are all enforced server-side by
// PrivatePortalGuard.
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { fetchPortals, issueAccessTicket } from './api';

const { t } = useI18n();
const route = useRoute();
const accountId = computed(() => route.params.accountId);

// Mirror the dashboard's resolved theme (themeHelper toggles `dark` on body) so
// the embedded portal renders in the same light/dark mode as the user setting.
const resolveTheme = () =>
  document.body.classList.contains('dark') ? 'dark' : 'light';
const currentTheme = ref(resolveTheme());
let themeObserver = null;

const portals = ref([]);
const isLoading = ref(true);
const isOpening = ref(false);
const errorMessage = ref('');
const selectedSlug = ref('');
const iframeSrc = ref('');
const isFrameReady = ref(false);

const onFrameLoad = () => {
  isFrameReady.value = true;
};

const selectedPortal = computed(
  () => portals.value.find(p => p.slug === selectedSlug.value) || null
);

const portalLocale = portal => portal?.meta?.default_locale || '';

const buildPortalUrl = portal => {
  const locale = portalLocale(portal);
  // Full portal layout (no show_plain_layout) so the hero search box, featured
  // articles and categories all render like the original public help center.
  // Same-origin iframe, so X-Frame-Options: SAMEORIGIN still allows embedding.
  // Pass theme so the portal follows the dashboard's light/dark setting.
  const base = locale ? `/hc/${portal.slug}/${locale}` : `/hc/${portal.slug}`;
  // embed=1 tells the portal to hide its own theme switcher (theme is driven by
  // the dashboard via the theme param).
  return `${base}?theme=${currentTheme.value}&embed=1`;
};

const openPortal = async portal => {
  if (!portal) return;
  isOpening.value = true;
  errorMessage.value = '';
  isFrameReady.value = false;
  try {
    // Issue/refresh the access ticket cookie before (re)loading the iframe.
    await issueAccessTicket(accountId.value);
    iframeSrc.value = buildPortalUrl(portal);
  } catch (e) {
    iframeSrc.value = '';
    errorMessage.value = t('CUSTOM.KB_BROWSER.OPEN_FAILED');
  } finally {
    isOpening.value = false;
  }
};

const loadPortals = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    portals.value = await fetchPortals(accountId.value);
    if (portals.value.length) {
      selectedSlug.value = portals.value[0].slug;
    }
  } catch (e) {
    errorMessage.value = t('CUSTOM.KB_BROWSER.LIST_FAILED');
  } finally {
    isLoading.value = false;
  }
};

// Whenever the selected portal changes (initial load or dropdown switch),
// re-issue the ticket and reload the iframe.
watch(selectedPortal, portal => {
  if (portal) openPortal(portal);
});

// Reload the iframe with the new theme when the dashboard theme changes.
watch(currentTheme, () => {
  if (selectedPortal.value) openPortal(selectedPortal.value);
});

onMounted(() => {
  loadPortals();
  themeObserver = new MutationObserver(() => {
    const next = resolveTheme();
    if (next !== currentTheme.value) currentTheme.value = next;
  });
  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onBeforeUnmount(() => {
  themeObserver?.disconnect();
  themeObserver = null;
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div
      v-if="portals.length > 1"
      class="flex items-center gap-2 px-6 py-2 border-b border-n-weak shrink-0"
    >
      <span class="text-xs text-n-slate-11">{{ t('CUSTOM.KB_BROWSER.PORTAL') }}</span>
      <select
        v-model="selectedSlug"
        class="px-2 py-1 text-sm bg-white border rounded-md border-n-weak text-n-slate-12"
      >
        <option
          v-for="portal in portals"
          :key="portal.id"
          :value="portal.slug"
        >
          {{ portal.name }}
        </option>
      </select>
    </div>

    <div
      v-if="errorMessage"
      class="px-6 py-3 text-sm text-n-ruby-11 bg-n-ruby-3"
    >
      {{ errorMessage }}
    </div>

    <div
      v-if="isLoading"
      class="flex items-center justify-center flex-1 text-sm text-n-slate-11"
    >
      {{ t('CUSTOM.COMMON.LOADING') }}
    </div>

    <div
      v-else-if="!portals.length"
      class="flex items-center justify-center flex-1 text-sm text-n-slate-11"
    >
      {{ t('CUSTOM.KB_BROWSER.EMPTY') }}
    </div>

    <div v-else-if="iframeSrc" class="relative flex-1 min-h-0">
      <iframe
        :src="iframeSrc"
        :title="t('CUSTOM.KB_BROWSER.TITLE')"
        class="absolute inset-0 w-full h-full border-0 transition-opacity duration-200"
        :class="isFrameReady ? 'opacity-100' : 'opacity-0'"
        @load="onFrameLoad"
      />
      <div
        v-if="!isFrameReady"
        class="absolute inset-0 flex items-center justify-center text-sm bg-n-background text-n-slate-11"
      >
        {{ t('CUSTOM.COMMON.LOADING') }}
      </div>
    </div>

    <div
      v-else-if="isOpening"
      class="flex items-center justify-center flex-1 text-sm text-n-slate-11"
    >
      {{ t('CUSTOM.KB_BROWSER.OPENING') }}
    </div>
  </div>
</template>
